<template>
    <div class="quiz-stats">
        <div class="quiz-stats-header">
            <h4><q-icon name="insights" /> Statistiques — {{ stats ? stats.title : '' }}</h4>
            <div>
                <q-btn v-if="stats" outline dense no-caps color="secondary" icon="download"
                    label="Exporter CSV" class="q-mr-sm" @click="exportCsv" />
                <q-btn icon="close" flat round dense v-close-popup />
            </div>
        </div>

        <div class="quiz-stats-body">
            <div v-if="loading" class="stats-state"><q-spinner size="42px" /></div>
            <div v-else-if="!stats" class="stats-state">Impossible de charger les statistiques.</div>
            <div v-else-if="!stats.responseCount" class="stats-state">Aucune réponse pour l'instant.</div>
            <template v-else>
                <div class="stats-summary">
                    <div class="stat-tile"><span class="stat-num">{{ stats.responseCount }}</span><span class="stat-lbl">réponses</span></div>
                    <div class="stat-tile"><span class="stat-num">{{ stats.respondentCount }}</span><span class="stat-lbl">répondants</span></div>
                    <div v-if="stats.mode !== 'survey'" class="stat-tile">
                        <span class="stat-num">{{ avgScoreLabel }}</span><span class="stat-lbl">score moyen</span>
                    </div>
                </div>

                <div v-for="(q, qi) in stats.questions" :key="q.id" class="stats-question">
                    <div class="stats-q-title">{{ qi + 1 }}. {{ q.text }}
                        <span class="stats-q-count">({{ q.answeredCount }} rép.)</span>
                    </div>

                    <template v-if="q.type === 'open'">
                        <ul class="stats-verbatims">
                            <li v-for="(o, oi) in q.openAnswers" :key="oi">{{ o.text }}</li>
                        </ul>
                        <p v-if="!q.openAnswers.length" class="stats-empty">Aucune réponse libre.</p>
                    </template>

                    <template v-else>
                        <div v-for="a in q.answers" :key="a.id" class="stats-bar-row">
                            <div class="stats-bar-label">
                                <q-icon v-if="a.is_correct && stats.mode !== 'survey'" name="check" size="18px" color="positive" />
                                {{ a.text }}
                            </div>
                            <div class="stats-bar-track">
                                <div class="stats-bar-fill" :style="{ width: pct(a.count, q.answeredCount) + '%' }"></div>
                            </div>
                            <div class="stats-bar-value">{{ a.count }} · {{ pct(a.count, q.answeredCount) }}%</div>
                        </div>
                    </template>
                </div>
            </template>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import { ref, computed, onMounted } from 'vue';

export default {
    name: 'QuizStats',
    props: { quizId: { required: true } },
    setup(props) {
        const loading = ref(true);
        const stats = ref(null);

        const fetchStats = async () => {
            loading.value = true;
            try {
                const url = `${process.env.VUE_APP_BASEURL}/quiz/${props.quizId}/stats`;
                stats.value = (await axios.get(url)).data;
            } catch (error) {
                console.log(error);
                stats.value = null;
            } finally {
                loading.value = false;
            }
        };

        const pct = (count, total) => total ? Math.round((100 * count) / total) : 0;
        const avgScoreLabel = computed(() => {
            if (!stats.value || stats.value.avgScore == null) return '—';
            return (Math.round(stats.value.avgScore * 10) / 10).toString();
        });

        const exportCsv = () => {
            window.open(`${process.env.VUE_APP_BASEURL}/quiz/${props.quizId}/responses.csv`, '_blank');
        };

        onMounted(fetchStats);
        return { loading, stats, pct, avgScoreLabel, exportCsv };
    },
};
</script>

<style scoped>
.quiz-stats { background: white; width: 100%; height: 100%; display: flex; flex-direction: column; }
.quiz-stats-header {
    display: flex; align-items: center; justify-content: space-between;
    padding: 8px 16px; border-bottom: 1px solid #e0e0e0;
}
.quiz-stats-header h4 { margin: 0; font-size: 20px; font-weight: 600; }
.quiz-stats-body { flex: 1; overflow-y: auto; padding: 16px; }
.stats-state { padding: 48px; text-align: center; color: #666; }
.stats-summary { display: flex; gap: 12px; margin-bottom: 20px; flex-wrap: wrap; }
.stat-tile {
    display: flex; flex-direction: column; align-items: center;
    border: 1px solid #eee; border-left: 3px solid var(--q-secondary);
    padding: 12px 22px; background: #fbfbfb; min-width: 120px;
}
.stat-num { font-size: 1.8rem; font-weight: 700; color: #1a1a1a; }
.stat-lbl { color: #777; font-size: 0.9rem; }
.stats-question { border: 1px solid #eee; padding: 14px 16px; margin-bottom: 12px; }
.stats-q-title { font-weight: 600; margin-bottom: 10px; }
.stats-q-count { color: #999; font-weight: 400; font-size: 0.9rem; }
.stats-bar-row { display: flex; align-items: center; gap: 10px; margin-bottom: 6px; }
.stats-bar-label { flex: 0 0 34%; display: flex; align-items: center; gap: 4px; }
.stats-bar-track { flex: 1; height: 18px; background: #f0f0f0; overflow: hidden; }
.stats-bar-fill { height: 100%; background: var(--q-secondary); transition: width .3s ease; }
.stats-bar-value { flex: 0 0 90px; text-align: right; color: #555; font-variant-numeric: tabular-nums; }
.stats-verbatims { margin: 0; padding-left: 18px; }
.stats-verbatims li { margin-bottom: 4px; }
.stats-empty { color: #999; font-style: italic; }
</style>
